import { DEV } from "solid-js"
import { type Object3D } from "three"
import type { Engine } from "../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../core/engine/EntityGraphNode/EntityGraphNode.type"
import entityDisplayName from "../../core/entity/entityDisplayName"
import { isEntityAPC, isEntityBuilding, isEntityDeadwood, isEntityFactory, isEntityForest, isEntityHQ, isEntityInfantry, isEntityJungle, isEntityLightRecon, isEntityLightTank, isEntityMediumArtillary, isEntityMediumCopter, isEntityMediumGunAntiAir, isEntityMediumMissileAntiAir, isEntityMediumRockets, isEntityMediumTank, isEntityMountain, isEntityRiver, isEntityRoad, isEntityShoal, isEntitySwamp, isEntityTransportCopter } from "../../core/entity/entityTypeMetaList.generated"
import Unexpected from "../../Exception/Unexpected.class"
import makeSingleTileMountainObject3D from "../objects/Mountain/makeSingleTileMountainObject3D"
import makeRiverForIconObject3D from "../objects/River/makeRiverForIconObject3D"
import makeShoalForIconObject3D from "../objects/Shoal/makeShoalForIconObject3D"
import isEntityTerrainMesh1 from "../objects/TerrainMesh1V2/isEntityTerrainMesh1"
import makeSingleTileTerrainMesh1Object3D from "../objects/TerrainMesh1V2/makeSingleTileTerrainMesh1Object3D"
import createDeadwoodGroup from "./createDeadwoodGroup"
import createForestGroup from "./createForestGroup"
import createJungleGroup from "./createJungleGroup"
import createSwampGroup from "./createSwampGroup"
import makeAPCScene from "./makeAPCScene"
import makeBuildingObject3D from "./makeBuildingObject3D"
import makeFactoryObject3D from "./makeFactoryObject3D"
import makeHQObject3D from "./makeHQObject3D"
import makeInfantryScene from "./makeInfantryScene"
import makeLightReconScene from "./makeLightReconScene"
import makeLightTankObject3D from "./makeLightTankObject3D"
import makeMediumArtilleryScene from "./makeMediumArtilleryScene"
import makeMediumCopterScene from "./makeMediumCopterScene"
import makeMediumGunAntiAirScene from "./makeMediumGunAntiAirScene"
import makeMediumMissileAntiAirScene from "./makeMediumMissileAntiAirScene"
import makeMediumRocketsScene from "./makeMediumRocketsScene"
import makeMediumTankScene from "./makeMediumTankScene"
import makePlaceHolderUnitGroup from "./makePlaceHolderUnitGroup"
import makeRoadMeshFromRoadEntity from "./makeRoadMeshFromRoadEntity"
import makeTransportCopterScene from "./makeTransportCopterScene"

export default function makeEntityGroup (
  engine: Engine,
  ent: EntityGraphNode,
  callback: (newGroup: Object3D) => void
): void {
  if (isEntityInfantry(ent)) {
    return makeInfantryScene(engine, ent, callback)
  }
  if (isEntityLightRecon(ent)) {
    return makeLightReconScene(engine, ent, callback)
  }
  if (isEntityLightTank(ent)) {
    return makeLightTankObject3D(engine, ent, callback)
  }
  if (isEntityMediumTank(ent)) {
    return makeMediumTankScene(engine, ent, callback)
  }
  if (isEntityMediumArtillary(ent)) {
    return makeMediumArtilleryScene(engine, ent, callback)
  }
  if (isEntityMediumGunAntiAir(ent)) {
    return makeMediumGunAntiAirScene(engine, ent, callback)
  }
  if (isEntityAPC(ent)) {
    return makeAPCScene(engine, ent, callback)
  }
  if (isEntityTransportCopter(ent)) {
    return makeTransportCopterScene(engine, ent, callback)
  }
  if (isEntityMediumCopter(ent)) {
    return makeMediumCopterScene(engine, ent, callback)
  }
  if (isEntityMediumRockets(ent)) {
    return makeMediumRocketsScene(engine, ent, callback)
  }
  if (isEntityMediumMissileAntiAir(ent)) {
    return makeMediumMissileAntiAirScene(engine, ent, callback)
  }
  if (isEntityRoad(ent)) {
    return makeRoadMeshFromRoadEntity(engine, ent, callback)
  }
  if (isEntityBuilding(ent)) {
    return makeBuildingObject3D(engine, ent, callback)
  }
  if (isEntityFactory(ent)) {
    return makeFactoryObject3D(engine, ent, callback)
  }
  if (isEntityHQ(ent)) {
    return makeHQObject3D(engine, ent, callback)
  }

  if (isEntityForest(ent)) {
    return createForestGroup(engine, ent, callback)
  }
  if (isEntityJungle(ent)) {
    return createJungleGroup(engine, ent, callback)
  }
  if (isEntitySwamp(ent)) {
    return createSwampGroup(engine, ent, callback)
  }
  if (isEntityDeadwood(ent)) {
    return createDeadwoodGroup(engine, ent, callback)
  }

  if (isEntityShoal(ent)) {
    return makeShoalForIconObject3D(callback)
  }

  // these are intended for icons only
  if (isEntityTerrainMesh1(ent)) {
    return makeSingleTileTerrainMesh1Object3D(ent, callback)
  }
  if (isEntityMountain(ent)) {
    return makeSingleTileMountainObject3D(ent, callback)
  }
  if (isEntityRiver(ent)) {
    return makeRiverForIconObject3D(callback)
  }

  if (DEV) {
    throw new Unexpected(`Unknown entity type: ${ent.etype_id} ${entityDisplayName(ent)}`)
  }
  return makePlaceHolderUnitGroup(engine, ent, callback)
}

